import {
  EColumnType,
  EFilterTypeField,
  EFilterValuePrefixes,
  TTableColumns,
} from '@/components/table/types';
import { getTranslate } from '@/helpers/translateHelpers';
import { TableColumnsData } from '@/services/dataServices';
import { tableFieldConvert } from '@/configs/tableConfigs';
import { TDropDownList, userRoles } from '@/types';

export const COLUMNS: TTableColumns = [
  {
    name: getTranslate('pages.reportBuilder.metrics.impressions'),
    field: tableFieldConvert('impressions'),
    useInFilter: false,
    type: EColumnType.number,
    visible: false,
  },
  {
    name: getTranslate('pages.reportBuilder.metrics.clicks'),
    field: tableFieldConvert('clicks'),
    useInFilter: false,
    type: EColumnType.number,
    visible: false,
  },
  {
    name: getTranslate('pages.reportBuilder.metrics.spend'),
    field: tableFieldConvert('spend'),
    useInFilter: false,
    type: EColumnType.number,
    visible: false,
  },
  {
    name: getTranslate('pages.reportBuilder.metrics.ctr'),
    field: tableFieldConvert('ctr'),
    useInFilter: false,
    type: EColumnType.number,
    visible: false,
  },
  {
    name: getTranslate('pages.reportBuilder.metrics.cpc'),
    field: tableFieldConvert('cpc'),
    useInFilter: false,
    type: EColumnType.number,
    visible: false,
  },
  {
    name: getTranslate('pages.reportBuilder.metrics.conversions'),
    field: tableFieldConvert('conversions'),
    useInFilter: false,
    type: EColumnType.number,
    visible: false,
  },
  {
    name: getTranslate('pages.reportBuilder.metrics.confirmed'),
    field: tableFieldConvert('confirmed'),
    useInFilter: false,
    type: EColumnType.number,
    visible: false,
  },
  {
    name: getTranslate('pages.reportBuilder.metrics.rejected'),
    field: tableFieldConvert('rejected'),
    useInFilter: false,
    type: EColumnType.number,
    visible: false,
  },
  {
    name: getTranslate('pages.reportBuilder.metrics.cpl'),
    field: tableFieldConvert('cpl'),
    useInFilter: false,
    type: EColumnType.number,
    visible: false,
  },
  {
    name: getTranslate('pages.reportBuilder.metrics.cpa'),
    field: tableFieldConvert('cpa'),
    useInFilter: false,
    type: EColumnType.number,
    visible: false,
  },
  {
    name: getTranslate('pages.reportBuilder.metrics.revenue'),
    field: tableFieldConvert('revenue'),
    useInFilter: false,
    type: EColumnType.number,
    visible: false,
  },
  {
    name: getTranslate('pages.reportBuilder.metrics.roi'),
    field: tableFieldConvert('roi'),
    useInFilter: false,
    type: EColumnType.number,
    visible: false,
  },
];

const FILTERS: TTableColumns = [
  {
    name: getTranslate('pages.reportBuilder.filters.group'),
    field: tableFieldConvert('group'),
    useInFilter: true,
    items: [],
    type: EColumnType.numberId,
    filterTypeField: EFilterTypeField.select,
    multiple: true,
    prefix: EFilterValuePrefixes.filter,
  },
  {
    name: getTranslate('pages.reportBuilder.filters.campaignIds'),
    field: tableFieldConvert('campaignIds'),
    useInFilter: true,
    type: EColumnType.numberId,
    filterTypeField: EFilterTypeField.input,
    multiple: true,
    prefix: EFilterValuePrefixes.filter,
  },
  {
    name: getTranslate('pages.reportBuilder.filters.creativeIds'),
    field: tableFieldConvert('creativeIds'),
    useInFilter: true,
    type: EColumnType.numberId,
    filterTypeField: EFilterTypeField.input,
    multiple: true,
    prefix: EFilterValuePrefixes.filter,
  },
  {
    name: getTranslate('pages.reportBuilder.filters.geo'),
    field: tableFieldConvert('geo'),
    useInFilter: true,
    items: [],
    type: EColumnType.text,
    filterTypeField: EFilterTypeField.select,
    prefix: EFilterValuePrefixes.filter,
  },
  {
    name: getTranslate('pages.reportBuilder.filters.user'),
    field: tableFieldConvert('campaignUserId'),
    useInFilter: true,
    items: [],
    role: userRoles.ADMIN,
    type: EColumnType.text,
    filterTypeField: EFilterTypeField.select,
    prefix: EFilterValuePrefixes.filter,
  },
];

const DIMENSIONS: TTableColumns = [
  {
    name: getTranslate('pages.reportBuilder.dimensions.timeUnit'),
    field: tableFieldConvert('timeUnit'),
    useInFilter: true,
    items: dimensionsTimeUnitItems(),
    prefix: EFilterValuePrefixes.dimension,
  },
  {
    name: getTranslate('pages.reportBuilder.dimensions.device'),
    field: tableFieldConvert('device'),
    useInFilter: true,
    items: dimensionsDeviceItems(),
    prefix: EFilterValuePrefixes.dimension,
  },
  {
    name: getTranslate('pages.reportBuilder.dimensions.geography'),
    field: tableFieldConvert('geography'),
    useInFilter: true,
    items: dimensionsGeographyItems(),
    prefix: EFilterValuePrefixes.dimension,
  },
  {
    name: getTranslate('pages.reportBuilder.dimensions.source'),
    field: tableFieldConvert('source'),
    useInFilter: true,
    items: dimensionsSourceItems(),
    prefix: EFilterValuePrefixes.dimension,
  },
  {
    name: getTranslate('pages.reportBuilder.dimensions.performance'),
    field: tableFieldConvert('performance'),
    useInFilter: true,
    items: dimensionsPerformanceItems(),
    prefix: EFilterValuePrefixes.dimension,
  },
  {
    name: getTranslate('pages.reportBuilder.dimensions.users'),
    field: tableFieldConvert('users'),
    useInFilter: true,
    items: dimensionsUserItems(),
    role: userRoles.ADMIN,
    prefix: EFilterValuePrefixes.dimension,
  },
];

const METRICS: TTableColumns = [
  {
    name: getTranslate('pages.reportBuilder.metrics.performance'),
    field: tableFieldConvert('performance'),
    useInFilter: true,
    items: metricPerformanceItems(),
    prefix: EFilterValuePrefixes.metric,
  },
  {
    name: getTranslate('pages.reportBuilder.metrics.postTracking'),
    field: tableFieldConvert('postTracking'),
    useInFilter: true,
    items: metricPostTrackingItems(),
    prefix: EFilterValuePrefixes.metric,
  },
];

export const columns: TableColumnsData = new TableColumnsData(COLUMNS);
export const filters: TableColumnsData = new TableColumnsData(FILTERS);
export const dimensions: TableColumnsData = new TableColumnsData(DIMENSIONS);
export const metrics: TableColumnsData = new TableColumnsData(METRICS);

export function metricPerformanceItems(): TDropDownList {
  return [
    {
      label: getTranslate('pages.reportBuilder.metrics.impressions'),
      value: tableFieldConvert('impressions'),
    },
    {
      label: getTranslate('pages.reportBuilder.metrics.clicks'),
      value: tableFieldConvert('clicks'),
    },
    {
      label: getTranslate('pages.reportBuilder.metrics.spend'),
      value: tableFieldConvert('spend'),
    },
    {
      label: getTranslate('pages.reportBuilder.metrics.ctr'),
      value: tableFieldConvert('ctr'),
    },
    {
      label: getTranslate('pages.reportBuilder.metrics.cpc'),
      value: tableFieldConvert('cpc'),
    },
  ];
}

export function metricPostTrackingItems(): TDropDownList {
  return [
    {
      label: getTranslate('pages.reportBuilder.metrics.conversions'),
      value: tableFieldConvert('conversions'),
    },
    {
      label: getTranslate('pages.reportBuilder.metrics.confirmed'),
      value: tableFieldConvert('confirmed'),
    },
    {
      label: getTranslate('pages.reportBuilder.metrics.rejected'),
      value: tableFieldConvert('rejected'),
    },
    {
      label: getTranslate('pages.reportBuilder.metrics.cpl'),
      value: tableFieldConvert('cpl'),
    },
    {
      label: getTranslate('pages.reportBuilder.metrics.cpa'),
      value: tableFieldConvert('cpa'),
    },
    {
      label: getTranslate('pages.reportBuilder.metrics.revenue'),
      value: tableFieldConvert('revenue'),
    },
    {
      label: getTranslate('pages.reportBuilder.metrics.roi'),
      value: tableFieldConvert('roi'),
    },
  ];
}

export function dimensionsTimeUnitItems(): TDropDownList {
  return [
    {
      label: getTranslate('pages.reportBuilder.dimensions.hour'),
      value: tableFieldConvert('hour'),
    },
    {
      label: getTranslate('pages.reportBuilder.dimensions.day'),
      value: tableFieldConvert('day'),
    },
    {
      label: getTranslate('pages.reportBuilder.dimensions.month'),
      value: tableFieldConvert('month'),
    },
  ];
}

export function dimensionsDeviceItems(): TDropDownList {
  return [
    {
      label: getTranslate('pages.reportBuilder.dimensions.deviceType'),
      value: tableFieldConvert('deviceType'),
    },
    {
      label: getTranslate('pages.reportBuilder.dimensions.operationSystem'),
      value: tableFieldConvert('operationSystem'),
    },
    {
      label: getTranslate('pages.reportBuilder.dimensions.browser'),
      value: tableFieldConvert('browser'),
    },
    {
      label: getTranslate('pages.reportBuilder.dimensions.make'),
      value: tableFieldConvert('make'),
    },
    {
      label: getTranslate('pages.reportBuilder.dimensions.model'),
      value: tableFieldConvert('model'),
    },
    {
      label: 'Screen resolution',
      value: tableFieldConvert('screenResolution'),
    },
  ];
}

export function dimensionsGeographyItems(): TDropDownList {
  return [
    {
      label: getTranslate('pages.reportBuilder.dimensions.country'),
      value: tableFieldConvert('country'),
    },
    {
      label: getTranslate('pages.reportBuilder.dimensions.region'),
      value: tableFieldConvert('region'),
    },
    {
      label: getTranslate('pages.reportBuilder.dimensions.city'),
      value: tableFieldConvert('city'),
    },
  ];
}

export function dimensionsSourceItems(): TDropDownList {
  return [
    {
      label: getTranslate('pages.reportBuilder.dimensions.inventoryType'),
      value: tableFieldConvert('inventoryType'),
    },
    {
      label: getTranslate('pages.reportBuilder.dimensions.inventorySource'),
      value: tableFieldConvert('inventorySource'),
    },
    {
      label: getTranslate('pages.reportBuilder.dimensions.domain'),
      value: tableFieldConvert('domain'),
    },
    {
      label: getTranslate('pages.reportBuilder.dimensions.categoryPage'),
      value: tableFieldConvert('categoryPage'),
    },
    {
      label: getTranslate('pages.reportBuilder.dimensions.languagePage'),
      value: tableFieldConvert('languagePage'),
    },
    {
      label: getTranslate('pages.reportBuilder.dimensions.referrerType'),
      value: tableFieldConvert('referrerType'),
    },
  ];
}

export function dimensionsPerformanceItems(): TDropDownList {
  return [
    {
      label: getTranslate('pages.reportBuilder.dimensions.groupId'),
      value: tableFieldConvert('groupId'),
    },
    {
      label: getTranslate('pages.reportBuilder.dimensions.campaignId'),
      value: tableFieldConvert('campaignId'),
    },
    {
      label: getTranslate('pages.reportBuilder.dimensions.creativeId'),
      value: tableFieldConvert('creativeId'),
    },
  ];
}

export function dimensionsUserItems(): TDropDownList {
  return [
    {
      label: getTranslate('pages.reportBuilder.dimensions.user'),
      value: tableFieldConvert('campaignUserId'),
    },
  ];
}
